import {gql} from '@apollo/client';
import React from 'react';

import {QueryResultType} from 'graphql-client/BenchlingQuery';

import {HOMEPAGE_SECTION_TEST_COMPONENT} from '../HomepageConstants';
import type {
  HomepageV2WorksheetsQuery,
  HomepageV2WorksheetsQueryVariables,
  HomepageV2WorksheetsQuery_worksheets_WorksheetNode as WorksheetNode,
} from './__generated/HomepageV2WorksheetsQuery';
import {MyWorkCategory} from './MyWorkConstants';
import {reviewableRowProps} from './MyWorkRowHelpers';
import {MyWorkRowsFooterAction, MyWorkRowsList} from './MyWorkRowsList';
import {useMyWorkRows} from './useMyWorkRows';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

export const HOMEPAGE_WORKSHEETS_QUERY = gql`
  query HomepageV2WorksheetsQuery($worksheetIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: NAVIGATION) {
    worksheets(ids: $worksheetIds) {
      ... on WorksheetNode {
        id
        name
        modifiedAt
        review {
          id
          status
        }
        editUrl
      }
      ... on InaccessibleInfoNode {
        invalidId
        canRequestAccess
      }
    }
  }
`;

/** Fetches and lists worksheets for the selected review-status filter. */
export default function WorksheetsRows({
  footerAction,
  selectedWorksheetIds,
  totalCount,
}: {
  footerAction?: MyWorkRowsFooterAction;
  selectedWorksheetIds: Array<string> | null;
  totalCount: number;
}): JSX.Element {
  const rowsResult = useMyWorkRows<
    HomepageV2WorksheetsQuery,
    HomepageV2WorksheetsQueryVariables,
    WorksheetNode
  >({
    query: HOMEPAGE_WORKSHEETS_QUERY,
    variables: {worksheetIds: selectedWorksheetIds!},
    skip: selectedWorksheetIds == null,
    getItems: (data) => data.worksheets,
    toRowProps: (item) => reviewableRowProps(item),
    errorMessage: 'Error loading worksheets',
    testComponent: TEST_COMPONENT,
  });

  if (rowsResult.type !== QueryResultType.SUCCESS) {
    return rowsResult.element;
  }

  return (
    <MyWorkRowsList
      rows={rowsResult.rows}
      category={MyWorkCategory.WORKSHEETS}
      totalCount={totalCount}
      footerAction={footerAction}
      emptyMessageDataTestAttrs={{
        component: TEST_COMPONENT,
        element: 'messageRow',
        key: 'emptyWorksheets',
      }}
    />
  );
}
